import { ChevronRight } from 'lucide-react';
import { FunctionComponent, JSX, ReactNode, SVGProps } from 'react';

import { Card } from '../../../shadcn/components/ui/card';
import { cn } from '../../../shadcn/lib/utils';
import { Header } from '../../atoms/header/header';
import { IllustrationName, illustrations } from '../../atoms/illustration/illustration';
import { Text } from '../../atoms/text/text';

type ContentCardProps = {
  readonly image: IllustrationName | FunctionComponent<SVGProps<SVGSVGElement>>;
  readonly stateIcon?: JSX.Element;
  readonly title: string;
  readonly description?: string;
  readonly supportInformation?: ReactNode;
  readonly onClick?: () => void;
  readonly disabled: boolean;
};

const ContentCard = ({
  description,
  disabled,
  image,
  onClick,
  stateIcon,
  supportInformation,
  title,
}: ContentCardProps) => {
  const Image = typeof image === 'string' ? illustrations[image] : image;

  const textStyling = disabled ? '!text-neutral-800' : '!text-abyssyl-blue-dark';

  return (
    <Card
      className={cn(
        'p-0 overflow-hidden w-full h-auto !shadow-none',
        disabled ? '!bg-neutral-200/80 !border !border-neutral-600' : '!bg-indigo-200/80'
      )}
    >
      <button
        className={cn('relative w-full min-h-[96px] min-w-[300px] flex justify-between', {
          'cursor-pointer': !disabled,
        })}
        disabled={disabled}
        onClick={onClick}
      >
        <div className="flex relative w-full items-center pl-2">
          <Image className={cn({ 'filter grayscale': disabled })} height={76} width={76} />
          <div className="pl-2 flex flex-col justify-start items-start max-w-3/4 z-10 text-left">
            <Header className={cn('text-left break-words hyphens-auto', textStyling)} level="h4">
              {title}
            </Header>
            {description && (
              <div className="flex flex-row gap-1 justify-center items-center">
                {stateIcon && <div className={disabled ? '!text-neutral-800' : '!text-indigo-700'}>{stateIcon}</div>}
                <Text className={textStyling} size="sm">
                  {description}
                </Text>
              </div>
            )}
            {supportInformation && (
              <Text className={textStyling} size="sm">
                {supportInformation}
              </Text>
            )}
          </div>
        </div>
        {!disabled && (
          <div className="flex items-center justify-center pr-2">
            <ChevronRight className="!text-teal-mid" size={28} />
          </div>
        )}
      </button>
    </Card>
  );
};

export { ContentCard };
export type { ContentCardProps };
